import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowBack, Refresh } from "@mui/icons-material";
import axios from "axios";
import { format, startOfMonth } from "date-fns";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";

// -1 = "All": the API returns every matching call in one page (page_size=all).
const ALL_ROWS = -1;
const PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500, { label: "All", value: ALL_ROWS }];

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return format(parsed, "dd MMM yyyy, h:mm a");
};

export default function CallLogsPage() {
  const history = useHistory();
  const { domain, token } = useSelector((state) => state.tokenReducer);
  const today = format(new Date(), "yyyy-MM-dd");
  const firstOfMonth = format(startOfMonth(new Date()), "yyyy-MM-dd");

  const [calls, setCalls] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ALL_ROWS);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    from: firstOfMonth,
    to: today,
    direction: "all",
    outcome: "",
  });

  const headers = useMemo(
    () => ({ Authorization: `token ${token}` }),
    [token]
  );

  const loadCalls = useCallback(async () => {
    if (!domain || !token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (rowsPerPage === ALL_ROWS) {
        params.set("page_size", "all");
      } else {
        params.set("page", page + 1);
        params.set("page_size", rowsPerPage);
      }
      if (filters.from) params.set("start_date", filters.from);
      if (filters.to) params.set("end_date", filters.to);
      params.set("timezone", Intl.DateTimeFormat().resolvedOptions().timeZone || "");
      if (filters.direction !== "all") params.set("direction", filters.direction);
      if (filters.outcome.trim()) params.set("outcome", filters.outcome.trim());

      const res = await axios.get(`${domain}crm/calls/?${params.toString()}`, {
        headers,
      });
      const data = res.data;
      setCalls(data?.results ?? (Array.isArray(data) ? data : []));
      setCount(data?.count ?? 0);
    } catch (error) {
      setCalls([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [domain, token, page, rowsPerPage, filters, headers]);

  useEffect(() => {
    loadCalls();
  }, [loadCalls]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => history.push("/admin/crm/dashboard-v2")}
            variant="outlined"
          >
            Dashboard
          </Button>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Call Logs
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {count.toLocaleString()} calls in the selected range
            </Typography>
          </Box>
        </Box>
        <Button startIcon={<Refresh />} onClick={loadCalls} disabled={loading}>
          Refresh
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <TextField
              label="From"
              type="date"
              value={filters.from}
              onChange={(e) => updateFilter("from", e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="To"
              type="date"
              value={filters.to}
              onChange={(e) => updateFilter("to", e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Direction</InputLabel>
              <Select
                label="Direction"
                value={filters.direction}
                onChange={(e) => updateFilter("direction", e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="outbound">Outbound</MenuItem>
                <MenuItem value="inbound">Inbound</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Outcome"
              value={filters.outcome}
              onChange={(e) => updateFilter("outcome", e.target.value)}
              placeholder="Answered, Brochure Sent..."
              fullWidth
              size="small"
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Direction</TableCell>
                <TableCell>Outcome</TableCell>
                <TableCell>Agent</TableCell>
                <TableCell>Duration</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : calls.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    No call logs found.
                  </TableCell>
                </TableRow>
              ) : (
                calls.map((call) => (
                  <TableRow key={call.id || call.call_uuid} hover>
                    <TableCell>{formatDateTime(call.start_time)}</TableCell>
                    <TableCell>{call.contact_name || "-"}</TableCell>
                    <TableCell>{call.company_name || "-"}</TableCell>
                    <TableCell>{call.phone_number || "-"}</TableCell>
                    <TableCell>
                      <Chip label={call.direction || "-"} size="small" />
                    </TableCell>
                    <TableCell>{call.outcome || call.status || "-"}</TableCell>
                    <TableCell>{call.agent_name || call.contact_agent || "-"}</TableCell>
                    <TableCell>{call.duration || "-"}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={count}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
        />
      </Paper>
    </Box>
  );
}
